import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalTypeaheadMenuPlugin, MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $createTextNode, $getSelection, $isRangeSelection, TextNode } from 'lexical';
import { createPortal } from 'react-dom';
import { $createMentionNode } from './MentionNode';
import { UserAvatar } from '@/features/users/UserAvatar';

/**
 * A person who can be @-mentioned on a reply. The parent assembles the
 * candidate list from the ticket (requester + watchers) and from
 * /api/users/agents (all IT staff), deduplicating by id.
 */
export interface MentionCandidate {
  id: string;
  /** Entra Object ID — used to fetch the MS profile photo in the dropdown. */
  entraObjectId: string;
  displayName: string;
  email: string;
}

class MentionOption extends MenuOption {
  // Declared explicitly; `erasableSyntaxOnly` in tsconfig disallows the
  // `constructor(public readonly x)` parameter-property shorthand.
  public readonly candidate: MentionCandidate;
  constructor(candidate: MentionCandidate) {
    super(candidate.id);
    this.candidate = candidate;
  }
}

/** Tracks @-mentions inside the editor — fires whenever the set of mentioned ids changes. */
export function useMentionTracking(
  editor: ReturnType<typeof useLexicalComposerContext>[0],
  onMentionsChanged?: (userIds: string[]) => void
) {
  useEffect(() => {
    if (!onMentionsChanged) return;
    const collect = () =>
      editor.getEditorState().read(() => {
        const ids = new Set<string>();
        const root = editor.getRootElement();
        if (!root) return;
        for (const span of Array.from(root.querySelectorAll<HTMLElement>('[data-user-id]'))) {
          const id = span.getAttribute('data-user-id');
          if (id) ids.add(id);
        }
        onMentionsChanged([...ids]);
      });
    return editor.registerUpdateListener(() => collect());
  }, [editor, onMentionsChanged]);
}

/**
 * Detects "@" + a partial query in the editor and shows a floating menu of
 * mentionable people. On pick, inserts a MentionNode token that survives JSON
 * round-trips and exports HTML as `<span data-user-id="...">@Name</span>`. The
 * backend resolves data-user-id to a user and narrows the reply email to them.
 *
 * Wired by RichTextEditor only when the parent passes candidates — i.e. when
 * the composer is rendered for an IT user on a ticket.
 */
export function MentionsPlugin({
  candidates,
  onMentionsChanged
}: {
  candidates: MentionCandidate[];
  onMentionsChanged?: (userIds: string[]) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState<string | null>(null);

  useMentionTracking(editor, onMentionsChanged);

  // Trigger: an "@" preceded by start-of-line or whitespace, followed by zero+
  // chars of the partial name. Bail out once the user types whitespace — that
  // ends the mention attempt and the menu closes.
  const checkForTrigger = useCallback((text: string) => {
    const match = /(^|\s)@([\p{L}\p{N}._-]{0,32})$/u.exec(text);
    if (!match) return null;
    return { leadOffset: match.index + match[1].length, matchingString: match[2], replaceableString: '@' + match[2] };
  }, []);

  const options = useMemo(() => {
    const q = (query ?? '').toLowerCase();
    return candidates
      .filter((c) => !q || c.displayName.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))
      .slice(0, 8)
      .map((c) => new MentionOption(c));
  }, [candidates, query]);

  const onSelectOption = useCallback(
    (selected: MentionOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const mention = $createMentionNode(`@${selected.candidate.displayName}`, selected.candidate.id);
        if (nodeToReplace) {
          nodeToReplace.replace(mention);
        } else {
          const sel = $getSelection();
          if ($isRangeSelection(sel)) sel.insertNodes([mention]);
        }
        // Trailing space so the next keystroke isn't absorbed by the mention token.
        mention.insertAfter($createTextNode(' '));
        mention.selectNext();
      });
      closeMenu();
    },
    [editor]
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionOption>
      onQueryChange={setQuery}
      onSelectOption={onSelectOption}
      triggerFn={checkForTrigger}
      options={options}
      menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
        if (anchorElementRef.current === null || options.length === 0) return null;
        return createPortal(
          <div className="rounded-md border bg-card shadow-lg overflow-hidden text-sm w-64 max-h-64 overflow-y-auto z-50">
            <ul role="listbox">
              {options.map((opt, i) => (
                <li
                  key={opt.candidate.id}
                  role="option"
                  aria-selected={i === selectedIndex}
                  onMouseEnter={() => setHighlightedIndex(i)}
                  onClick={() => selectOptionAndCleanUp(opt)}
                  className={
                    'cursor-pointer px-3 py-2 flex items-center gap-2 ' +
                    (i === selectedIndex ? 'bg-primary/10 text-primary' : 'hover:bg-muted')
                  }
                >
                  <UserAvatar oid={opt.candidate.entraObjectId} name={opt.candidate.displayName} sizeClass="w-7 h-7" />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{opt.candidate.displayName}</div>
                    <div className="text-xs text-muted-foreground truncate">{opt.candidate.email}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>,
          anchorElementRef.current
        );
      }}
    />
  );
}
